import { useState, useEffect, useRef, useCallback, type AnchorHTMLAttributes, type HTMLAttributes, type ReactNode } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Search } from "lucide-react";
import { MDXProvider } from "@mdx-js/react";
import { categories, docs } from "./loadDocs";
import * as demoComponents from "../Updates/demos";
import "../Updates/updates-prose.css";

function slugify(text: string) {
	return text.toLowerCase().replace(/[^\w\s-]/g, "").replace(/\s+/g, "-");
}

function MdxLink({ href, children, ...props }: AnchorHTMLAttributes<HTMLAnchorElement>) {
	const navigate = useNavigate();
	const isInternal = href?.startsWith("/");
	if (isInternal) {
		return (
			<a {...props} href={href} onClick={(e) => { e.preventDefault(); navigate(href!); }}>
				{children}
			</a>
		);
	}
	return <a href={href} target="_blank" rel="noopener noreferrer" {...props}>{children}</a>;
}

function MdxH2({ children, ...props }: HTMLAttributes<HTMLHeadingElement>) {
	const text = extractText(children);
	const id = slugify(text);
	return <h2 id={id} {...props}>{children}</h2>;
}

function extractText(node: ReactNode): string {
	if (typeof node === "string") return node;
	if (typeof node === "number") return String(node);
	if (Array.isArray(node)) return node.map(extractText).join("");
	if (node && typeof node === "object" && "props" in node) return extractText(node.props.children);
	return "";
}

interface Heading { id: string; text: string }

function useHeadings(contentRef: React.RefObject<HTMLDivElement | null>, slug: string) {
	const [headings, setHeadings] = useState<Heading[]>([]);
	useEffect(() => {
		if (!contentRef.current) return;
		const els = contentRef.current.querySelectorAll("h2[id]");
		setHeadings(Array.from(els).map((el) => ({ id: el.id, text: el.textContent ?? "" })));
	}, [slug, contentRef]);
	return headings;
}

function useActiveHeading(scrollRef: React.RefObject<HTMLElement | null>, headings: Heading[]) {
	const [activeId, setActiveId] = useState<string>("");
	useEffect(() => {
		const container = scrollRef.current;
		if (!container || headings.length === 0) return;
		const onScroll = () => {
			const offset = container.scrollTop + 100;
			let current = headings[0]?.id ?? "";
			for (const h of headings) {
				const el = document.getElementById(h.id);
				if (el && el.offsetTop <= offset) current = h.id;
			}
			setActiveId(current);
		};
		onScroll();
		container.addEventListener("scroll", onScroll, { passive: true });
		return () => container.removeEventListener("scroll", onScroll);
	}, [scrollRef, headings]);
	return activeId;
}

const mdxComponents = { a: MdxLink, h2: MdxH2, ...demoComponents };

export default function DocsView() {
	const navigate = useNavigate();
	const location = useLocation();
	const slugFromUrl = location.pathname.split("/").filter(Boolean)[1] || "";
	const selectedSlug = slugFromUrl || docs[0]?.slug || "";
	const selected = docs.find((d) => d.slug === selectedSlug) ?? docs[0];

	const setSelectedSlug = useCallback((slug: string) => {
		navigate(`/docs/${slug}`, { replace: true });
	}, [navigate]);

	const [search, setSearch] = useState("");
	const contentRef = useRef<HTMLDivElement>(null);
	const mainRef = useRef<HTMLElement>(null);
	const headings = useHeadings(contentRef, selected?.slug ?? "");
	const activeId = useActiveHeading(mainRef, headings);

	// Scroll content to top on article change
	useEffect(() => {
		mainRef.current?.scrollTo({ top: 0 });
	}, [selectedSlug]);

	const scrollTo = useCallback((id: string) => {
		const el = document.getElementById(id);
		const container = mainRef.current;
		if (el && container) {
			container.scrollTo({ top: el.offsetTop - 24, behavior: "smooth" });
		}
	}, []);

	// Filter articles by search
	const filteredCategories = search.trim()
		? categories
			.map((cat) => ({
				...cat,
				articles: cat.articles.filter(
					(a) =>
						a.frontmatter.title.toLowerCase().includes(search.toLowerCase()) ||
						(a.frontmatter.description ?? "").toLowerCase().includes(search.toLowerCase()),
				),
			}))
			.filter((cat) => cat.articles.length > 0)
		: categories;

	if (!selected) return null;

	return (
		<div className="flex w-full h-full overflow-hidden">
			{/* Sidebar */}
			<aside className="w-[240px] min-w-[240px] border-r border-border py-4 overflow-y-auto flex flex-col">
				{/* Search */}
				<div className="px-3 mb-4">
					<div className="relative">
						<Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground" />
						<input
							type="text"
							placeholder="Search docs..."
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							className="w-full h-8 pl-8 pr-3 rounded-md border border-input bg-background text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
						/>
					</div>
				</div>

				{/* Category nav */}
				<nav className="flex flex-col gap-4 px-2 flex-1">
					{filteredCategories.map((cat) => (
						<div key={cat.name}>
							<h3 className="text-[0.6875rem] font-semibold uppercase tracking-wider text-muted-foreground px-2 mb-1.5">
								{cat.name}
							</h3>
							<div className="flex flex-col gap-0.5">
								{cat.articles.map((article) => (
									<button
										key={article.slug}
										type="button"
										onClick={() => setSelectedSlug(article.slug)}
										className={`flex flex-col gap-0.5 px-3 py-2 border-none rounded-md text-left cursor-pointer transition-colors ${
											article.slug === selected.slug
												? "bg-accent"
												: "bg-transparent hover:bg-accent"
										}`}
									>
										<span className="text-[0.8125rem] font-medium text-foreground">
											{article.frontmatter.title}
										</span>
										{article.frontmatter.description && (
											<span className="text-[0.6875rem] text-muted-foreground line-clamp-1">
												{article.frontmatter.description}
											</span>
										)}
									</button>
								))}
							</div>
						</div>
					))}
					{filteredCategories.length === 0 && (
						<p className="text-sm text-muted-foreground text-center py-8">No articles match "{search}"</p>
					)}
				</nav>
			</aside>

			{/* Content */}
			<main ref={mainRef} className="flex-1 overflow-y-auto py-8 px-[clamp(24px,4vw,64px)]">
				<div ref={contentRef} className="max-w-[720px]">
					<span className="text-[0.6875rem] font-medium uppercase tracking-wider text-muted-foreground">
						{selected.frontmatter.category}
					</span>
					<h1 className="text-2xl font-semibold text-foreground mb-6">
						{selected.frontmatter.title}
					</h1>
					<MDXProvider components={mdxComponents}>
						<div className="updates-prose">
							<selected.Component />
						</div>
					</MDXProvider>
				</div>
			</main>

			{/* Table of Contents */}
			{headings.length > 0 && (
				<aside className="w-[200px] min-w-[200px] border-l border-border py-8 px-4 overflow-y-auto hidden xl:block">
					<h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">
						On this page
					</h3>
					<nav className="flex flex-col gap-1">
						{headings.map((h) => (
							<button
								key={h.id}
								type="button"
								onClick={() => scrollTo(h.id)}
								className={`text-left text-[0.75rem] leading-snug px-2 py-1 rounded border-none cursor-pointer transition-colors ${
									activeId === h.id
										? "text-primary font-medium bg-primary/5"
										: "text-muted-foreground bg-transparent hover:text-foreground"
								}`}
							>
								{h.text}
							</button>
						))}
					</nav>
				</aside>
			)}
		</div>
	);
}
